import type { IsoDate, IsoWeekday } from './types.ts'

const MS_PER_DAY = 86_400_000
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Toda la aritmetica de fechas se hace sobre medianoche UTC.
 *
 * Es deliberado: en horario local de Madrid, la semana del cambio de hora dura
 * 167 o 169 horas, y dividir por 7*24h da resultados equivocados justo en las
 * semanas del cambio. Sobre UTC, un dia mide siempre exactamente 24 h y los
 * turnos de rotacion salen bien todo el año.
 *
 * Estas funciones trabajan con fechas civiles ('YYYY-MM-DD'), no con instantes.
 * La conversion a instante real (para cron y horas limite) es responsabilidad
 * de la capa que conoce la zona horaria del hogar.
 */

export function parseIsoDate(date: IsoDate): number {
  const m = ISO_DATE_RE.exec(date)
  if (!m) throw new TypeError(`Fecha ISO invalida: ${JSON.stringify(date)}`)
  const [, y, mo, d] = m
  const year = Number(y)
  const month = Number(mo)
  const day = Number(d)
  const ms = Date.UTC(year, month - 1, day)
  const back = new Date(ms)
  // Rechaza fechas que existen en el formato pero no en el calendario
  // (2026-02-30 se desbordaria silenciosamente a marzo).
  if (
    back.getUTCFullYear() !== year ||
    back.getUTCMonth() !== month - 1 ||
    back.getUTCDate() !== day
  ) {
    throw new RangeError(`Fecha inexistente: ${date}`)
  }
  return ms
}

export function formatIsoDate(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return formatIsoDate(parseIsoDate(date) + days * MS_PER_DAY)
}

/** Dias completos de `from` a `to`. Negativo si `to` es anterior. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parseIsoDate(to) - parseIsoDate(from)) / MS_PER_DAY)
}

/** Dia de la semana ISO: 1 = lunes … 7 = domingo. */
export function dayOfWeek(date: IsoDate): IsoWeekday {
  const jsDay = new Date(parseIsoDate(date)).getUTCDay() // 0 = domingo
  return (jsDay === 0 ? 7 : jsDay) as IsoWeekday
}

/** Modulo que devuelve siempre un resultado no negativo. */
export function floorMod(n: number, m: number): number {
  if (m <= 0) throw new RangeError(`Modulo debe ser positivo, recibido ${m}`)
  return ((n % m) + m) % m
}

/**
 * Inicio de la semana a la que pertenece `date`, siendo `startDay` el dia en
 * que arranca (5 = viernes). Un jueves cae en la semana que empezo el viernes
 * anterior.
 */
export function weekStartFor(date: IsoDate, startDay: IsoWeekday): IsoDate {
  const back = floorMod(dayOfWeek(date) - startDay, 7)
  return addDays(date, -back)
}

/** Ultimo dia de la semana (inicio + 6). */
export function weekEndFor(weekStart: IsoDate): IsoDate {
  return addDays(weekStart, 6)
}

/** Los 7 dias de la semana, en orden. */
export function weekDays(weekStart: IsoDate): IsoDate[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
}

export function isWithinWeek(date: IsoDate, weekStart: IsoDate): boolean {
  const diff = daysBetween(weekStart, date)
  return diff >= 0 && diff <= 6
}

/** Numero de semanas completas entre dos inicios de semana. Puede ser negativo. */
export function weeksBetween(fromWeekStart: IsoDate, toWeekStart: IsoDate): number {
  return Math.floor(daysBetween(fromWeekStart, toWeekStart) / 7)
}
